import { Allocation as AllocationType } from "@/api/SolarCloudApi";
import { ArrowDown, ArrowUp, Pencil, Trash2 } from "lucide-react";
import UpdateAllocationDialog from "./UpdateAllocationDialog";
import DeleteAllocationDialog from "./DeleteAllocationDialog";
import PanelCard from "./PanelCard";

type AllocationProps = {
  allocation: AllocationType;
  unallocatedWatts: number;
  isFirst?: boolean;
  isLast?: boolean;
  account: "gas" | "electricity" | "bank" | undefined;
  deleteAllocation: (allocation: AllocationType) => void;
  updateAllocation: (allocation: AllocationType) => void;
  moveAllocation: (
    allocation: AllocationType,
    direction: "up" | "down"
  ) => void;
};

export default function AllocationCard({
  allocation,
  unallocatedWatts,
  isFirst = false,
  isLast = false,
  account,
  deleteAllocation,
  updateAllocation,
  moveAllocation,
}: AllocationProps) {
  const panels = allocation.wattCapacity! / 100;
  const lastPayout = allocation.energyBills?.[0]?.amount;
  return (
    <div className="flex flex-col items-center my-12 shadow-lg">
      <h3 className="text-2xl text-brand-orange-500 pb-1">
        {allocation.nickname}
      </h3>
      <PanelCard
        panels={panels}
        capacity={allocation.wattCapacity!}
        className="-mb-4 z-10"
      />
      <div className="bg-brand-neutral-650 grid grid-cols-1 md:grid-cols-2 w-full min-h-40 py-4 rounded-2xl relative z-0">
        <div className="absolute top-0 bottom-0 right-2 flex flex-col justify-center">
          {!isFirst && (
            <button
              onClick={() => moveAllocation(allocation, "up")}
              className="cursor-pointer text-brand-neutral-500 hover:text-white"
            >
              <ArrowUp className="size-5" />
            </button>
          )}
          {!isLast && (
            <button
              onClick={() => moveAllocation(allocation, "down")}
              className="cursor-pointer text-brand-neutral-500 hover:text-white"
            >
              <ArrowDown className="size-5" />
            </button>
          )}
        </div>
        {allocation.energyCompanyPayee && (
          <EnergyCompanyPayeeCard allocation={allocation} />
        )}
        {allocation.bankPayee && <BankPayeeCard allocation={allocation} />}
        {allocation.payPalPayee && <PaypalPayeeCard allocation={allocation} />}
        <div className="flex flex-col items-center justify-center col-span-1 gap-2 p-1">
          <p className="text-sm">Last payout</p>
          {lastPayout ? (
            <>
              <p className="text-brand-orange-500 text-2xl">
                <span className="text-sm">$</span>
                {lastPayout}
              </p>
            </>
          ) : (
            <p className="text-sm text-brand-neutral-500">No payouts yet</p>
          )}
        </div>
        <div className="flex gap-8 w-full md:w-1/2 items-center justify-center absolute -bottom-8 md:-bottom-5 left-0">
          <UpdateAllocationDialog
            allocation={allocation}
            unallocatedWatts={unallocatedWatts}
            handleUpdateAllocation={updateAllocation}
            account={account}
          />
          <DeleteAllocationDialog
            allocation={allocation}
            handleDeleteAllocation={deleteAllocation}
            account={account}
          />
        </div>
      </div>
    </div>
  );
}

function EnergyCompanyPayeeCard({
  allocation,
}: {
  allocation: AllocationType;
}) {
  if (!allocation.energyCompanyPayee) return null;

  return (
    <div className="flex flex-col items-center justify-center col-span-1 gap-2 p-1">
      <p className="text-sm">Energy Company</p>
      <p className="text-brand-orange-500 text-2xl">
        {allocation.energyCompanyPayee.energyCompany?.name?.toLowerCase() === "other"
          ? allocation.energyCompanyPayee.otherName
          : allocation.energyCompanyPayee.energyCompany?.name}
      </p>
    </div>
  );
}

function BankPayeeCard({ allocation }: { allocation: AllocationType }) {
  if (!allocation.bankPayee) return null;

  return (
    <div className="flex flex-col items-center justify-center col-span-1 gap-2 p-1">
      <p className="text-sm">Bank Account</p>
      <p className="text-brand-orange-500 text-2xl text-center">
        {allocation.bankPayee.accountName}
      </p>
    </div>
  );
}

function PaypalPayeeCard({ allocation }: { allocation: AllocationType }) {
  if (!allocation.payPalPayee) return null;

  return (
    <div className="flex flex-col items-center justify-center col-span-1 gap-2 p-1">
      <p className="text-sm">PayPal Account</p>
      <p className="text-brand-orange-500 text-2xl text-center">
        {allocation.payPalPayee.email}
      </p>
    </div>
  );
}
